import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "alta_cart_v2";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return parsed.filter((i) => i && i.key);
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [surgeryDate, setSurgeryDate] = useState(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // System bundle (may be customized).
  const addItem = (product, qty = 1, extras = {}) => {
    setItems((prev) => {
      const key = "system:" + product.id + "|" + JSON.stringify(extras.variants || {});
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [
        ...prev,
        {
          key,
          kind: "system",
          id: product.id,
          name: product.name,
          subtitle: product.subtitle,
          price: extras.price ?? product.price,
          image: product.image,
          variants: extras.variants || {},
          swaps: extras.swaps || [],
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  // Standalone refill SKU.
  const addRefill = (refill, qty = 1) => {
    setItems((prev) => {
      const key = "refill:" + refill.id;
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [
        ...prev,
        {
          key,
          kind: "refill",
          id: refill.id,
          name: refill.name,
          subtitle: `${refill.brand} · ${refill.category}`,
          price: refill.price,
          image: refill.image,
          brand: refill.brand,
          quantity: qty,
        },
      ];
    });
    setOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const updateQty = (key, delta) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i))
        .filter(Boolean)
    );
  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{
        items, addItem, addRefill, removeItem, updateQty, clear, subtotal, count,
        open, setOpen, surgeryDate, setSurgeryDate,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
